import type { ExclusionCounts, QualityClass, SessionStatus, StudyStatus } from "@gazelab/shared";

const dateFmt = new Intl.DateTimeFormat("es-MX", { dateStyle: "medium", timeStyle: "short" });

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : dateFmt.format(d);
}

export function fmtNum(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return v.toLocaleString("es-MX", { maximumFractionDigits: digits, minimumFractionDigits: 0 });
}

/** Fracción 0–1 → porcentaje legible. */
export function fmtPct(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return `${(v * 100).toLocaleString("es-MX", { maximumFractionDigits: digits })} %`;
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export const STUDY_STATUS_LABELS: Record<StudyStatus, string> = {
  draft: "Borrador",
  active: "Activo",
  closed: "Cerrado",
  archived: "Archivado",
};

export const SESSION_STATUS_LABELS: Record<SessionStatus, string> = {
  created: "Creada",
  calibrating: "Calibrando",
  running: "En curso",
  completed: "Completada",
  aborted: "Cancelada",
  interrupted: "Interrumpida",
};

export const QUALITY_LABELS: Record<QualityClass, string> = {
  pass: "Aprobada",
  warn: "Advertencia",
  fail: "Fallida",
};

export const EXCLUSION_LABELS: Record<keyof ExclusionCounts, string> = {
  manual: "Exclusión manual",
  calibrationFailed: "Validación fallida / sin validar",
  calibrationWarnFiltered: "Validación con advertencia (filtrada)",
  noCompletedTrial: "Sin ensayo completado de esta imagen",
  insufficientValidSamples: "Muestras válidas insuficientes",
  sessionNotCompleted: "Sesión no completada",
};

/** Nombre de archivo seguro (sin espacios ni caracteres especiales). */
export function safeFilename(...parts: string[]): string {
  return parts
    .map((p) =>
      p
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-zA-Z0-9_-]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase(),
    )
    .filter(Boolean)
    .join("_");
}

/** Duración legible (s o min:s). */
export function fmtDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return "—";
  const s = Math.round(ms / 100) / 10;
  if (s < 60) return `${s.toLocaleString("es-MX")} s`;
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.round(s - m * 60)).padStart(2, "0")} min`;
}
